import {
  useCachedReportWithData,
  useCurrentWorkspace,
  firestoreWorkspaceCollectionRef,
  useWorkspaceStore,
  useCachedReports,
  useIsAdminOrManagerRole,
} from '@easy-expense/data-firestore-client';
import { entityFields } from '@easy-expense/data-firestore-shared';
import {
  ReportWithData,
  ReportWithDataSchema,
  ReportSchema,
  EntityFilter,
} from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation, useIntlStore } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Separator, Spacer } from '@easy-expense/ui-web-core';
import {
  DefaultReportTemplate,
  EmptyEntityFilter,
  filterData,
  getReportPDFProps,
  renderToTemplate,
  reportHeadText,
  ReportPDFTemplateProps,
} from '@easy-expense/utils-shared';
import { Popover } from '@mui/material';
import cuid from 'cuid';
import _ from 'lodash';
import React from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { NavigateOptions } from 'react-router-dom';

import { DownloadReportCSVButton } from './DownloadReportCSVButton';
import { DownloadReportPDFButton } from './DownloadReportPDFButton';
import { ReportName } from './ReportName.component';
import { ReportNotes } from './ReportNotes.component';
import { auth } from '../../../firebase/app';
import {
  MeteredPaywallActions,
  useHandleMeteredAction,
} from '../../../hooks/useHandleMeteredAction';
import { ReportApprovalSection } from '../../Approvals/ReportApprovalSection.component';
import { Button } from '../../Button.components';
import { ExpenseTable } from '../../Expense/MaterialExpenseTable.component';
import { CloseButton } from '../../Shared/CloseButton.component';
import { SearchDropdown } from '../../Shared/Dropdown/SearchDropDown.component';
import { TripTable } from '../../Trip/TripList';
import { ReportAddTransactions } from '../ReportAddTransactions.component';
import { ReportAddTrips } from '../ReportAddTrips.component';
import { ShareReport } from '../ShareReportButton';

export const useReportPreviewData = () => {
  const tripClasses = Data.tripClasses.use();
  const vendors = Data.vendors.use();
  const categories = Data.expenseCategories.use();
  const paymentMethods = Data.paymentMethods.use();
  const locations = Data.locations.use();
  const workspaceRateDetails = Data.rateDetails.use();
  const countryRateDetails = Data.countryRateDetails.useCountryRate();

  const members = useWorkspaceStore((s) => s.workspaceMembers);
  const reports = useCachedReports();
  const clients = Data.clients.use();

  const incomeCategories = Data.incomeCategories.use();

  const getReportPreview = ({
    reportWithData,
    entityFilter,
  }: {
    reportWithData: ReportWithData;
    entityFilter?: EntityFilter;
  }) => {
    const filter = entityFilter ?? EmptyEntityFilter;
    const expenses = Data.expenses.getByKeys(reportWithData.expenses);
    const incomes = Data.incomes.getByKeys(reportWithData.incomes);
    const trips = Data.trips.getByKeys(reportWithData.trips);
    return filterData({
      incomes,
      expenses,
      trips,
      reports,
      categories,
      incomeCategories,
      vendors,
      paymentMethods,
      tripClasses,
      locations,
      members,
      clients,
      filter,
      workspaceRateDetails,
      countryRateDetails,
    });
  };

  return { getReportPreview };
};

export const ReportDetail: React.FC<React.PropsWithChildren<{ reportKey?: string }>> = ({
  reportKey,
}) => {
  const hideTrips = Data.trips.getSize() === 0;
  const navigate = useNavigate();
  const cachedReport = useCachedReportWithData(reportKey);
  const workspace = useCurrentWorkspace();
  const { formatCurrency } = useIntlStore();
  const clients = Data.clients.use();
  const [clientName, setClientName] = React.useState<string | null>(
    cachedReport?.clientValue?.name ?? null,
  );
  const [report, setReport] = React.useState<ReportWithData | null>(cachedReport);
  const [reportChanged, setReportChanged] = React.useState<boolean>(false);
  const [isNewReport, setIsNewReport] = React.useState<boolean>(false);

  const [taxRate, secondTaxRate] = _.filter(workspace?.settings.taxRates, (tr) => tr.tracked);
  const showReimbursable = workspace?.settings.showReimbursementsToggle ?? false;

  const [user] = useAuthState(auth);
  const handleMeteredCreateReport = useHandleMeteredAction(MeteredPaywallActions.CREATE_REPORT);
  const [saveURL, setSaveURL] = React.useState<string | null>(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = React.useState<HTMLElement | null>(null);
  const { getReportPreview } = useReportPreviewData();
  const isAdminOrManager = useIsAdminOrManagerRole();
  const isMemberLockedByApproval =
    !isAdminOrManager &&
    (report?.approvalStatus === 'pending' ||
      report?.approvalStatus === 'approved' ||
      report?.approvalStatus === 'rejected');

  //done so that the fetchReportSummary is not called when status is updated since it does not appear on the pdf
  // The Add/Remove Transactions picker mode lives in the URL (?add=transactions)
  // so it survives opening a transaction and returning to it (see EZ-4974).
  const [searchParams, setSearchParams] = useSearchParams();
  const viewAddTransactions = searchParams.get('add') === 'transactions';
  const setViewAddTransactions = React.useCallback(
    (value: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set('add', 'transactions');
          } else {
            next.delete('add');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [viewAddTrips, setViewAddTrips] = React.useState<boolean>();

  const [reportHtml, setReportHtml] = React.useState<string>('');
  const locations = Data.locations.use();

  React.useEffect(() => {
    if (cachedReport) {
      setReport(cachedReport);
      setClientName(cachedReport.clientValue?.name ?? null);
      reloadReportPreview(cachedReport);
    }
  }, [cachedReport, workspace?.org]);

  React.useEffect(() => {
    if (!reportKey && !cachedReport && user) {
      const newReportWithData = ReportWithDataSchema.safeParse({
        ...entityFields('create', user?.uid),
        name: '',
        status: 'Unsent',
        expenses: [],
        incomes: [],
        trips: [],
        key: cuid(),
        tripTotal: 0,
        expenseTotal: 0,
        incomeTotal: 0,
        total: 0,
        reportExpenses: [],
        reportIncomes: [],
        reportTrips: [],
        canEdit: true,
      });

      if (newReportWithData.success) {
        setIsNewReport(true);
        setReport(newReportWithData.data);
        reloadReportPreview(newReportWithData.data);

        return;
      }

      throw `error creating a new report with data ${JSON.stringify(newReportWithData, null, 2)}`; // open the error page
    }
  }, [cachedReport, user, reportKey]);

  React.useEffect(() => {
    if (report && !viewAddTransactions && !viewAddTrips) {
      reloadReportPreview(report);
    }
  }, [viewAddTransactions, viewAddTrips]);

  const screenWidth = window.innerWidth > 0 ? window.innerWidth : screen.width;

  function onDelete() {
    if (reportKey) {
      if (confirm(getTranslation('Are you sure you want to permanently delete this report?'))) {
        Data.reports.archive(reportKey);
        navigate(`/reports`);
      }
    } else {
      navigate(`/reports`);
    }
  }

  /**
   * Writes the current draft to Firestore, returning whether the write went through. Callers
   * changing report fields of their own (submitting for approval) pass them as `overrides` so
   * the draft and their change land in a single write.
   */
  async function saveReportDraft(overrides?: Partial<ReportWithData>) {
    if (!isNewReport) {
      return writeReportDraft(overrides);
    }
    let saved = false;
    await handleMeteredCreateReport(async () => {
      saved = await writeReportDraft(overrides);
    });
    return saved;
  }

  async function writeReportDraft(overrides?: Partial<ReportWithData>) {
    try {
      // `clientName` is populated for any report that already has a client, so creating
      // unconditionally would mint a duplicate client (fresh cuid) on every save and repoint
      // the report at it. Only create when the name doesn't match a client we already have.
      let clientKey = report?.client;
      if (clientName) {
        const existingClient = clients.find((client) => client.value.name === clientName);
        clientKey = existingClient?.key ?? Data.clients.create({ value: { name: clientName } });
      }

      /**
       * This is a hack to get around being able to set the createBy field when updating entities
       * This will be fixed in https://linear.app/easy-expense/issue/EE-3149/extend-the-report-data-type-to-include-shared-userid
       */

      const entityfields = isNewReport
        ? { ...entityFields('create', user?.uid ?? '') }
        : { ...entityFields('update', user?.uid ?? '') };

      const key = report?.key ?? cuid();
      const parsedReport = ReportSchema.partial().parse({
        ...report,
        ...entityfields,
        client: clientKey,
        key,
        ...overrides,
      });
      const workspaceCollectionRef = firestoreWorkspaceCollectionRef('report');
      workspaceCollectionRef.doc(key).set(parsedReport);

      /* hack end */

      // Everything in local state is now persisted, so leaving shouldn't warn about unsaved
      // changes. The report also stops being new once written, or a later save on this same
      // screen (submit for approval doesn't navigate away) would stamp `create` fields again.
      setReportChanged(false);
      setIsNewReport(false);

      return true;
    } catch (err) {
      alert('There was an error saving the report');
      return false;
    }
  }

  // Takes no arguments on purpose: it's wired straight to a click handler, and any parameter
  // here would be filled by the click event.
  async function onSaveReport() {
    // Saving a report nobody edited still stamped a new updatedAt and updatedBy, so the reports
    // list recorded whoever last opened a report as the person who last changed it, and sorted
    // it to the top ahead of reports people had actually worked on. Leaving without writing is
    // what the user asked for either way. A brand new report is the exception: it exists only in
    // local state until its first save, so skipping that one would discard it.
    if (!reportChanged && !isNewReport) {
      navigate(`/reports`);
      return;
    }
    const saved = await saveReportDraft();
    if (saved) {
      navigate(`/reports`);
    }
  }

  async function reloadReportPreview(reportToPreview: ReportWithData) {
    if (workspace === undefined) {
      return undefined;
    }
    const data = getReportPreview({ reportWithData: reportToPreview });
    const reportHtml = renderToTemplate<ReportPDFTemplateProps>(
      reportHeadText,
      DefaultReportTemplate,
      getReportPDFProps({
        data,
        attachReceipts: true,
        workspace,
        reportKey,
        isPreview: true,
        taxRate,
        secondTaxRate,
        showReimbursable,
      }),
    );

    setReportHtml(reportHtml);
  }

  function closeReportDetail(navPath: string = '/reports', navOptions?: NavigateOptions) {
    if (reportChanged) {
      if (
        confirm(
          getTranslation('There are unsaved changes on this report, do you wish to discard it?'),
        )
      ) {
        navigate(navPath, navOptions);
      }
    } else {
      navigate(navPath, navOptions);
    }
  }

  if (!report) {
    return null;
  }

  const reimbursableExpenses = report.reportExpenses.filter((expense) => expense.isReimbursable);
  const unreimbursedExpenses = reimbursableExpenses.filter(
    (expense) => expense.reimbursableState !== 'reimbursed',
  );
  const needsReimbursement = unreimbursedExpenses.length > 0;
  const hasBeenReimbursed = reimbursableExpenses.length > 0 && unreimbursedExpenses.length === 0;
  const reimburseTotal = reimbursableExpenses.reduce(
    (sum, expense) => sum + Math.abs(expense.total),
    0,
  );
  let reimburseTextColor: 'warningDark' | 'successDark' | undefined;
  if (needsReimbursement) {
    reimburseTextColor = 'warningDark';
  } else if (hasBeenReimbursed) {
    reimburseTextColor = 'successDark';
  }

  if (viewAddTransactions) {
    return (
      <ReportAddTransactions
        onClose={() => setViewAddTransactions(false)}
        report={report}
        setReportDirty={() => {
          setReportChanged(true);
          setSaveURL(null);
        }}
      />
    );
  }

  if (viewAddTrips) {
    return (
      <ReportAddTrips
        onClose={() => setViewAddTrips(false)}
        report={report}
        setReportDirty={() => {
          setReportChanged(true);
          setSaveURL(null);
        }}
      />
    );
  }

  return (
    <Layout.Column style={{ width: '100%', height: '100vh' }}>
      <Layout.Row>
        {screenWidth > 1000 ? (
          <Layout.Column
            style={{
              display: 'block',
              backgroundColor: '#CFD6E0',
              overflow: 'scroll',
              height: '100vh',
              minWidth: '700px',
            }}
            px={24}
            py={20}
            className="hide-on-mobile"
          >
            <Layout.Column style={{ height: '100%' }}>
              <iframe
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: 8,
                  boxShadow: '0 0 16px rgba(31, 31, 31, 0.3)',
                }}
                src={'data:text/html;charset=utf-8,' + encodeURIComponent(reportHtml)}
              />
            </Layout.Column>
          </Layout.Column>
        ) : null}

        <Layout.Column
          style={{
            height: '100vh',
            boxShadow: '-8px 0 8px rgba(31, 31, 31, 0.1)',
          }}
          grow
        >
          <Layout.Column py={24} px={32} grow style={{ overflow: 'scroll', width: '100%' }}>
            <Layout.Row>
              <Spacer.Flex />
              <Layout.Column style={{ minWidth: 650, maxWidth: 750, width: '100%' }}>
                <Layout.Row>
                  <Layout.Column grow>
                    <ReportName
                      name={report.name}
                      onChange={(name) => {
                        setReport({ ...report, name });
                        setReportChanged(true);
                        setSaveURL(null);
                      }}
                    />
                  </Layout.Column>

                  <Spacer.Horizontal size={16} />

                  <Layout.Column grow>
                    <ShareReport
                      createdBy={report.shared ? 'everyone' : report.createdBy}
                      onSelected={(shareWith) => {
                        if (shareWith?.key) {
                          if (shareWith.key === 'everyone') {
                            setReport({ ...report, shared: true });
                          } else {
                            setReport({ ...report, shared: false, createdBy: shareWith.key });
                          }
                          setReportChanged(true);
                          setSaveURL(null);
                        }
                      }}
                    />
                  </Layout.Column>
                </Layout.Row>

                <Spacer.Vertical size={16} />
                <ReportApprovalSection report={report} onSaveReport={saveReportDraft} />

                <Spacer.Vertical size={16} />

                <Layout.Row>
                  <Layout.Column grow>
                    <SearchDropdown
                      header={getTranslation('Client')}
                      placeholder={getTranslation('Client Name')}
                      selectedKey={report.client}
                      autoCompleteValues={clients}
                      onSelect={(clientName) => {
                        setReportChanged(true);
                        setSaveURL(null);
                        if (clientName?.key) {
                          setReport((prev) =>
                            ReportWithDataSchema.parse({ ...prev, client: clientName.key }),
                          );

                          setClientName(clientName.value.name);
                        } else if (clientName?.value.name) {
                          setClientName(clientName.value.name);
                        }
                      }}
                      allowCreateNew
                      clearOnSelect={true}
                      iconicIcon="person-circle-outline"
                      error={false}
                    />
                  </Layout.Column>

                  <Spacer.Horizontal size={16} />

                  <Layout.Column grow>
                    <SearchDropdown
                      header={getTranslation('Location')}
                      placeholder={getTranslation('Location Name')}
                      selectedKey={report.location}
                      autoCompleteValues={[
                        {
                          key: undefined,
                          value: { name: 'All Locations', iconicIcon: 'map-outline' },
                        },
                        ...locations,
                      ]}
                      onSelect={(newLocation) => {
                        if (newLocation) {
                          setReport({ ...report, location: newLocation.key ?? null });
                          setReportChanged(true);
                          setSaveURL(null);
                        }
                      }}
                      error={false}
                      iconicIcon="map-outline"
                    />
                  </Layout.Column>
                </Layout.Row>

                <Spacer.Vertical size={16} />

                <ReportNotes
                  desc={report.desc}
                  onChange={(desc) => {
                    setReport({ ...report, desc });
                    setReportChanged(true);
                    setSaveURL(null);
                  }}
                />

                <Spacer.Vertical size={32} />

                <Layout.Row
                  border={[1, 'solid', 'grayLight']}
                  radius={8}
                  bg="white"
                  style={{ width: '100%' }}
                >
                  <Layout.Column grow px={16} py={16}>
                    <Layout.Row align>
                      <OpenSans.Primary size="s-16">🚗</OpenSans.Primary>
                      <Spacer.Horizontal size={6} />
                      <OpenSans.Secondary weight="bold-700" size="s-16">
                        {getTranslation('Mileage')}
                      </OpenSans.Secondary>
                    </Layout.Row>
                    <Spacer.Vertical size={8} />
                    <OpenSans.Primary weight="bold" size="xl-28">
                      {formatCurrency(Math.abs(report?.tripTotal ?? 0))}
                    </OpenSans.Primary>
                  </Layout.Column>

                  <Separator.Vertical />

                  <Layout.Column grow px={16} py={16}>
                    <Layout.Row align>
                      <OpenSans.Primary size="s-16">🧾</OpenSans.Primary>
                      <Spacer.Horizontal size={6} />
                      <OpenSans.Secondary weight="bold-700" size="s-16">
                        {getTranslation('Expenses')}
                      </OpenSans.Secondary>
                    </Layout.Row>
                    <Spacer.Vertical size={8} />
                    <OpenSans.Primary weight="bold" size="xl-28">
                      {formatCurrency(Math.abs(report?.expenseTotal ?? 0))}
                    </OpenSans.Primary>
                  </Layout.Column>

                  {report?.incomeTotal ? (
                    <>
                      <Separator.Vertical />

                      <Layout.Column grow px={16} py={16}>
                        <Layout.Row align>
                          <OpenSans.Primary size="s-16">📈</OpenSans.Primary>
                          <Spacer.Horizontal size={6} />
                          <OpenSans.Secondary weight="bold-700" size="s-16">
                            {getTranslation('Income')}
                          </OpenSans.Secondary>
                        </Layout.Row>
                        <Spacer.Vertical size={8} />
                        <OpenSans.Primary weight="bold" size="xl-28">
                          {formatCurrency(Math.abs(report?.incomeTotal ?? 0))}
                        </OpenSans.Primary>
                      </Layout.Column>
                    </>
                  ) : null}

                  <Separator.Vertical />

                  <Layout.Column grow px={16} py={16}>
                    <Layout.Row align>
                      <OpenSans.Primary size="s-16">💸</OpenSans.Primary>
                      <Spacer.Horizontal size={6} />
                      <OpenSans.Secondary weight="bold-700" size="s-16">
                        {getTranslation('Reimburse')}
                      </OpenSans.Secondary>
                    </Layout.Row>
                    <Spacer.Vertical size={8} />
                    <OpenSans.Primary weight="bold" size="xl-28" color={reimburseTextColor}>
                      {formatCurrency(reimburseTotal)}
                    </OpenSans.Primary>
                  </Layout.Column>
                </Layout.Row>

                <Spacer.Vertical size={16} />

                <Layout.Row py>
                  <OpenSans.Primary weight="bold" size={32}>
                    {getTranslation('Transactions')}
                  </OpenSans.Primary>
                  <Spacer.Horizontal size={32} />

                  <Layout.PressableRow
                    onClick={() => setViewAddTransactions(true)}
                    bg="white"
                    border={[1, 'solid', 'brandPrimary']}
                    px
                    radius={100}
                    align
                  >
                    <Icon name="add-outline" size={16} color={theme.colors.brandPrimary} />
                    <Spacer.Horizontal size={8} />
                    <OpenSans.Pressable>
                      {getTranslation('Add/Remove Transactions')}
                    </OpenSans.Pressable>
                  </Layout.PressableRow>
                </Layout.Row>

                <Spacer.Vertical size={16} />

                <ExpenseTable
                  onRowClick={(expenseKey) =>
                    closeReportDetail(`/expense/${expenseKey}`, {
                      state: { returnTo: `/report/${report.key}` },
                    })
                  }
                  expenseList={report.expenses}
                  visibleColumns={[
                    'date',
                    'vendorValueName',
                    'categoryValueName',
                    'approvalStatus',
                    'total',
                  ]}
                  additionalOptions={{ enableTopToolbar: false }}
                  hideDateYear
                />

                {!hideTrips ? (
                  <Layout.Column>
                    <Layout.Row py>
                      <OpenSans.Primary size={32}>{getTranslation('Drives')}</OpenSans.Primary>

                      <Spacer.Horizontal size={16} />

                      <Layout.PressableRow
                        onClick={() => setViewAddTrips(true)}
                        bg="brandPrimaryXLight"
                        border={[1, 'solid', 'brandPrimary']}
                        px
                        radius={100}
                        align
                      >
                        <Icon name="add-outline" size={16} color={theme.colors.brandPrimary} />
                        <Spacer.Horizontal size={8} />
                        <OpenSans.Pressable>
                          {getTranslation('Add/Remove Drives')}
                        </OpenSans.Pressable>
                      </Layout.PressableRow>
                    </Layout.Row>

                    <Spacer.Vertical size={32} />

                    <TripTable
                      onRowClick={() => {}}
                      tripList={report.trips}
                      visibleColumns={['date', 'tripClassValueName', 'trip', 'distance', 'total']}
                      additionalOptions={{ enableTopToolbar: false }}
                    />
                  </Layout.Column>
                ) : null}
              </Layout.Column>

              <Spacer.Flex />
            </Layout.Row>
          </Layout.Column>

          <Layout.Row
            style={{
              borderTop: `2px solid ${theme.colors.grayXLight}`,
              width: '100%',
            }}
            bg="white"
            py={24}
          >
            <Spacer.Flex />

            <Layout.Row align style={{ minWidth: 650, maxWidth: 750, width: '100%' }}>
              {!isMemberLockedByApproval ? (
                <>
                  <Layout.PressableRow
                    px
                    py={8}
                    bg="destructiveXLight"
                    radius={100}
                    border={[2, 'solid', 'white']}
                    align
                    onClick={onDelete}
                  >
                    <Icon name="trash-outline" size={20} color={theme.colors.destructiveDark} />
                    <Spacer.Horizontal size={8} />
                    <OpenSans.Primary color="destructiveDark" grow size="s-16">
                      {getTranslation('Delete Report')}
                    </OpenSans.Primary>
                    <Spacer.Horizontal size={4} />
                  </Layout.PressableRow>

                  <Spacer.Flex />
                </>
              ) : null}

              {!isNewReport ? (
                <>
                  <Layout.PressableRow
                    px
                    py={8}
                    bg="inputBackground"
                    border={[1, 'solid', 'inputBorder']}
                    radius={100}
                    align
                    onClick={(e: React.MouseEvent<HTMLElement>) =>
                      setDownloadMenuAnchor(e.currentTarget)
                    }
                  >
                    <Icon name="cloud-download-outline" size={20} color={theme.colors.primary} />
                    <Spacer.Horizontal size={8} />
                    <OpenSans.Primary size="s-16">{getTranslation('Download')}</OpenSans.Primary>
                  </Layout.PressableRow>

                  <Popover
                    open={Boolean(downloadMenuAnchor)}
                    anchorEl={downloadMenuAnchor}
                    onClose={() => setDownloadMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  >
                    <Layout.Column py={8} px={8} style={{ minWidth: 160 }}>
                      <DownloadReportPDFButton
                        reportID={report.key}
                        saveURL={saveURL}
                        setSaveURL={setSaveURL}
                      />

                      <Spacer.Vertical size={8} />

                      <DownloadReportCSVButton reportID={report.key} />
                    </Layout.Column>
                  </Popover>

                  <Spacer.Horizontal size={16} />
                </>
              ) : null}

              {!isMemberLockedByApproval ? (
                <Button.Primary
                  onClick={onSaveReport}
                  radius={50}
                  style={{
                    border: `2px solid ${theme.colors.white}`,
                    marginRight: 24,
                  }}
                >
                  <OpenSans.Custom
                    size={15}
                    weight="bold-700"
                    style={{ color: theme.colors.buttonWhite }}
                  >
                    {getTranslation('Save Report')}
                  </OpenSans.Custom>
                  <Icon
                    size={15}
                    color={theme.colors.buttonWhite}
                    style={{ paddingLeft: 10 }}
                    name="chevron-forward"
                  />
                </Button.Primary>
              ) : null}
            </Layout.Row>

            <Spacer.Flex />
          </Layout.Row>
        </Layout.Column>
      </Layout.Row>
      <CloseButton onClose={() => closeReportDetail()} />
    </Layout.Column>
  );
};
